import { useAPIMutation, useAPIQuery } from '../useAPIProvider';
import { EffectiveDate } from '../contracts/catalog/types';

export const useBaseMargins = () => {
  return useAPIQuery(['getBaseMargins'], async client => {
    const response = await client.catalog.products.getBaseMargins();
    if (response.status !== 200) throw new Error('There is an error');
    return response.body.items;
  });
};

export const useBaseMargin = (id: string) => {
  return useAPIQuery(['getBaseMargin', id], async client => {
    const response = await client.catalog.products.getBaseMargin({
      params: { productId: id },
      query: { date: new Date() }
    });
    if (response.status !== 200) throw new Error('There is an error');
    return response.body;
  });
};

export const usePrice = (id: string) => {
  return useAPIQuery(['getPrice', id], async client => {
    const response = await client.catalog.products.getPrice({ params: { productId: id }, query: { date: new Date() } });
    if (response.status !== 200) throw new Error('There is an error');
    return response.body;
  });
};

export const usePrices = () => {
  return useAPIQuery(['getPrices'], async client => {
    const response = await client.catalog.products.getPrices();
    if (response.status !== 200) throw new Error('There is an error');
    return response.body.items;
  });
};

interface AddPriceAndMarginArgs {
  productId: string;
  price: number;
  modifier: number;
  min: number;
  max: number;
  effectiveDate: EffectiveDate;
}

export const useAddPriceAndMargin = () => {
  return useAPIMutation(
    (client, query) =>
      async ({ productId, price, effectiveDate, max, min, modifier }: AddPriceAndMarginArgs) => {
        const priceResponse = await client.catalog.products.addPrice({
          body: {
            daasCatalogItemId: productId,
            price,
            effectiveDate
          }
        });

        const marginResponse = await client.catalog.products.addBaseMargin({
          body: {
            daasCatalogItemId: productId,
            modifier,
            min,
            max,
            effectiveDate
          }
        });

        if (priceResponse.status !== 200 || marginResponse.status !== 200) {
          throw new Error('Failed to add price and margin');
        }

        await query.invalidateQueries(['getProducts']);
        await query.invalidateQueries(['getProductPriceHistory', productId]);
        await query.invalidateQueries(['getBaseMargin', productId]);
        await query.invalidateQueries(['getPrice', productId]);
        await query.invalidateQueries(['getBaseMargins']);
        await query.invalidateQueries(['getPrices']);
        return;
      }
  );
};
